import { Button } from '@/components/ui/button';
import WhatsAppMessagePreview from '@/components/WhatsAppMessagePreview';
import PreviewRecipientSelect from '@/components/PreviewRecipientSelect';
import type { PreviewTemplate } from '@/lib/whatsappPreview';
import type { CampaignLaunchState } from '@/hooks/useCampaignLaunchState';

interface LaunchPreviewProps {
  state: CampaignLaunchState;
  /** Jumps back to the media step. */
  onChangeMedia?: () => void;
}

/**
 * The message, as WhatsApp will render it.
 *
 * Sticky because the whole point is to watch it change while the step on the left is being filled
 * in — a preview you have to scroll back to is a preview nobody looks at.
 */
export default function LaunchPreview({ state, onChangeMedia }: LaunchPreviewProps) {
  const {
    channel, selectedTemplate, requirements, previewRecipients, previewRecipientId,
    setPreviewRecipientId, previewRecipient, chosenAsset, needsMediaHeader, headerFormat,
    uploadedMediaName, headerText, buttonValues, customMessage, defaultMessage, refreshHeaderPreviews,
  } = state;

  return (
    <div className="lg:sticky lg:top-6 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Preview</h3>
        {onChangeMedia && (
          <Button variant="ghost" size="sm" onClick={onChangeMedia}>
            Change media
          </Button>
        )}
      </div>

      {channel === 'whatsapp' ? (
        <>
          <PreviewRecipientSelect
            recipients={previewRecipients}
            value={previewRecipientId}
            onChange={setPreviewRecipientId}
          />
          <WhatsAppMessagePreview
            template={selectedTemplate as PreviewTemplate | null}
            requirements={requirements}
            recipient={previewRecipient}
            headerMedia={chosenAsset ? {
              kind: chosenAsset.kind,
              previewUrl: chosenAsset.previewUrl,
              label: chosenAsset.label,
            } : (needsMediaHeader ? {
              kind: headerFormat === 'VIDEO' ? 'video' : headerFormat === 'DOCUMENT' ? 'document' : 'photo',
              previewUrl: null,
              note: uploadedMediaName
                ? `${uploadedMediaName} — uploaded`
                : 'No media chosen yet',
            } : null)}
            onMediaError={chosenAsset ? refreshHeaderPreviews : undefined}
            headerText={headerText}
            buttonValues={buttonValues}
          />
        </>
      ) : (
        <WhatsAppMessagePreview smsBody={customMessage || defaultMessage || 'No message yet'} />
      )}
    </div>
  );
}
